import {produce} from 'immer';
import styled from 'styled-components';
import toast from '@/components/HotToast';
import {Typography} from '@/components/Typography';
import {NearbyOptionItem} from '@/map/@types';
import {interceptProps} from '@/styled-components';
import {palette} from '@/styles';
import {RoundColorContent} from '../../Markers/RoundColorMarker/RoundColorContent';

export interface DataboxProps {
  type: '업종' | '정보';
  limit?: number;
  options: NearbyOptionItem[];
  selected: string[];
  onSelected(value: string[]): void;
}

export const DataSelectBox = ({
  type,
  options,
  selected = [],
  limit,
  onSelected,
}: DataboxProps) => {
  const onClick =
    ({id, active}: {id: string; active: boolean}) =>
    () => {
      if (limit && active && selected && selected?.length + 1 > limit) {
        toast.error(`${type}은 최대 ${limit}개까지 선택할 수 있습니다.`);
        return;
      }

      const index = selected && selected.findIndex((i) => i === id);
      const nextSelected = produce(selected, (draft) => {
        if (active && index === -1) {
          draft.push(id);
        } else if (index >= 0) {
          draft.splice(index, 1);
        }
      });

      onSelected(nextSelected);
    };

  return (
    <Options>
      {options.map((i) => (
        <Item
          key={i.id}
          active={selected.includes(i.id)}
          onClick={onClick({id: i.id, active: !selected.includes(i.id)})}
        >
          <ItemIcon
            color={i.icon ? 'transparent' : i.color}
            name={i.iconicStr ?? i.label}
            icon={i.icon}
          />
          <ItemName>{i.label}</ItemName>
        </Item>
      ))}
    </Options>
  );
};

const Item = styled('button').withConfig({
  shouldForwardProp: interceptProps(['active']),
})<{
  active?: boolean;
}>`
  width: 48px;
  height: 48px;
  border-radius: 5px;
  cursor: pointer;
  ${({active}) =>
    active &&
    `background-color: ${palette.gray100};
  border: solid 1px ${palette.gray200};`}
`;

const ItemIcon = styled(RoundColorContent)`
  margin: auto;
`;

const ItemName = styled(Typography).attrs({
  variant: 'medium10',
  as: 'div',
})`
  font-weight: 500;
  margin-top: 2px;
  color: ${palette.gray600};
`;

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;
